import { useCallback, useState } from 'react';
import useApiFetch from '@/hooks/useApiFetch';
import Attachments from '@/services/attachments';
import technologiesServices from '@/services/technologies';
import { FetchApiIds } from '@/types';
import { UploadedCandidateAttachments } from '@/types/api';
import {
  AIInput,
  DEFAULT_SYSTEM_MESSAGE,
  promptsPerSection,
} from './useAutofillFromResume.prompts';
import { useOpenAi } from '../useOpenAi';

export type ExtractableSection =
  | 'summary'
  | 'skills'
  | 'education'
  | 'experience'
  | 'basicInfo'
  | 'projects';

interface UseAutofillFromResumeReturn {
  isLoading: boolean;
  error: Error | null;
  extractFromResume: (section: ExtractableSection) => Promise<string>;
  getTechnologieMatch: (techName: string) => string;
}

export const useAutofillFromResume = (candidateId: string): UseAutofillFromResumeReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const technologiesResponse = useApiFetch(
    FetchApiIds.ALL_TECHS,
    technologiesServices.getAllTechnologies
  );

  const { generateCompletion } = useOpenAi({
    systemMessage: DEFAULT_SYSTEM_MESSAGE,
  });

  const getAllCandidateAttachments = useApiFetch<UploadedCandidateAttachments, string>(
    [FetchApiIds.GET_ALL_ATTACHMENTS, candidateId],
    async () => Attachments.listAttachments(candidateId)
  );

  const getLatestResume = useCallback(() => {
    const resumeAttachments = getAllCandidateAttachments.data?.RESUME ?? [];
    if (resumeAttachments.length === 0) {
      throw new Error('No resume found');
    }

    return resumeAttachments.reduce((latest, current) => {
      return new Date(current.updatedAt) > new Date(latest.updatedAt) ? current : latest;
    });
  }, [getAllCandidateAttachments.data]);

  const getPromptForSection = (section: ExtractableSection): AIInput => {
    return promptsPerSection[section];
  };

  const extractFromResume = async (section: ExtractableSection): Promise<string> => {
    setIsLoading(true);
    setError(null);

    try {
      const latestResume = getLatestResume();
      const { prompt, systemMessage } = getPromptForSection(section);

      return await generateCompletion(prompt, latestResume.url, { systemMessage });
    } catch (err) {
      const extractError = err instanceof Error ? err : new Error('Failed to extract resume data');
      setError(extractError);
      throw extractError;
    } finally {
      setIsLoading(false);
    }
  };

  const getTechnologieMatch = useCallback(
    (techName: string) => {
      const technologies = technologiesResponse.data || [];

      const technology = technologies?.find((t) => t.name.toLowerCase() === techName.toLowerCase());
      return technology ? technology.id : '';
    },
    [technologiesResponse.data]
  );

  return {
    isLoading: isLoading || getAllCandidateAttachments.isLoading,
    error: error || getAllCandidateAttachments.error,
    extractFromResume,
    getTechnologieMatch,
  };
};
